export class DecryptService {
  private readonly IMG_KEY_B64 = '6CFYc+kGWZT0CSDTx/1+ag==';

  /* ================== UTILS ================== */

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private async normalizeKey(b64Key: string): Promise<CryptoKey> {
    const raw = this.base64ToArrayBuffer(b64Key);
    const rawBytes = new Uint8Array(raw);

    let keyBytes: Uint8Array;
    if (rawBytes.length === 16) {
      keyBytes = rawBytes;
    } else if (rawBytes.length > 16) {
      keyBytes = rawBytes.slice(0, 16);
    } else {
      keyBytes = new Uint8Array(16);
      keyBytes.set(rawBytes);
    }

    return crypto.subtle.importKey(
      'raw',
      keyBytes.buffer,
      { name: 'AES-CBC' },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  /* ================== ENCRYPT ================== */

  /**
   * Chiffre un Uint8Array → Uint8Array = IV(16) + Ciphertext
   */
  async encryptBuffer(data: Uint8Array): Promise<Uint8Array> {
    const key = await this.normalizeKey(this.IMG_KEY_B64);
    const iv = crypto.getRandomValues(new Uint8Array(16));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-CBC', iv },
      key,
      data,
    );

    const result = new Uint8Array(iv.length + encrypted.byteLength);
    result.set(iv);
    result.set(new Uint8Array(encrypted), iv.length);
    return result;
  }

  /* ================== DECRYPT ================== */

  /**
   * Décrypte Uint8Array = IV(16) + Ciphertext
   */
  async decryptBuffer(encrypted: Uint8Array): Promise<Uint8Array> {
    const key = await this.normalizeKey(this.IMG_KEY_B64);

    const iv = encrypted.slice(0, 16);
    const ciphertext = encrypted.slice(16);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      key,
      ciphertext,
    );

    return new Uint8Array(decrypted);
  }

  /**
   * Décrypte une string Base64 → string UTF-8
   */
  async decryptString(encryptedB64: string): Promise<string> {
    const encrypted = new Uint8Array(this.base64ToArrayBuffer(encryptedB64));
    const decrypted = await this.decryptBuffer(encrypted);

    return new TextDecoder().decode(decrypted);
  }

  /**
   * Décrypte JSON Base64 → Object
   */
  async decryptJson<T = any>(encryptedB64: string): Promise<T> {
    const decryptedText = await this.decryptString(encryptedB64);
    return JSON.parse(decryptedText);
  }
}